/**
 * Creator Studio - Assets Library Page
 *
 * Full-page browser for the shared asset library. Designers can preload
 * logos, photography, SVG icons, and brand elements before starting a
 * project. Supports folders / subfolders, favorites, compression, and
 * admin-only delete.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Image as ImageIcon,
  Upload,
  Search,
  Settings,
  Loader2,
  Shapes,
} from 'lucide-react';
import { useAssetStore } from '../stores/assetStore';
import type { AssetRecord, AssetType } from '../types';
import AssetFolderList from '../components/editor/asset/AssetFolderList';
import AssetTile from '../components/editor/asset/AssetTile';
import AssetUploadQueue from '../components/editor/asset/AssetUploadQueue';
import AssetCompressionSettings from '../components/editor/asset/AssetCompressionSettings';

const TYPE_OPTIONS: { id: AssetType | null; label: string }[] = [
  { id: null, label: 'All Types' },
  { id: 'image', label: 'Images' },
  { id: 'svg', label: 'SVGs' },
  { id: 'icon', label: 'Icons' },
];

export default function AssetsPage() {
  const {
    assets,
    folders,
    isLoading,
    searchQuery,
    typeFilter,
    activeFolderId,
    favoritesOnly,
    canDelete,
    loadAssets,
    loadFolders,
    refreshUserRole,
    uploadMany,
    setSearchQuery,
    setTypeFilter,
  } = useAssetStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadAssets();
    loadFolders();
    refreshUserRole();
  }, [loadAssets, loadFolders, refreshUserRole]);

  const currentFolder = useMemo(
    () => folders.find((f) => f.id === activeFolderId) || null,
    [folders, activeFolderId]
  );

  const filteredAssets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return assets.filter((a) => {
      if (favoritesOnly && !a.is_favorite) return false;
      if (!favoritesOnly && activeFolderId && a.folder_id !== activeFolderId) return false;
      if (typeFilter && a.asset_type !== typeFilter) return false;
      if (q) {
        const hay = `${a.name} ${a.file_name} ${(a.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [assets, searchQuery, typeFilter, activeFolderId, favoritesOnly]);

  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter(
        (f) => f.type.startsWith('image/') || /\.svg$/i.test(f.name)
      );
      if (arr.length === 0) return;
      await uploadMany(arr, activeFolderId);
    },
    [uploadMany, activeFolderId]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [handleUpload]
  );

  const handleInsert = (asset: AssetRecord) => {
    if (asset.thumbnail_url) {
      navigator.clipboard?.writeText(asset.thumbnail_url).catch(() => {});
    }
  };

  return (
    <div className="flex h-full overflow-hidden bg-[#0a0a0a]">
      <aside className="w-64 flex-shrink-0 flex flex-col bg-[#0f0f0f] border-r border-white/[0.06] overflow-y-auto sidebar-scroll">
        <div className="px-4 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Shapes size={15} className="text-amber-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">Asset Library</h2>
              <p className="text-[10px] text-zinc-600">Shared across all designers</p>
            </div>
          </div>
        </div>
        <AssetFolderList />
        <div className="flex-1" />
        <div className="p-3 border-t border-white/[0.06]">
          <button
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] text-zinc-400 hover:bg-white/[0.04] transition-colors"
          >
            <Settings size={12} />
            Compression settings
          </button>
          {canDelete && (
            <p className="mt-2 px-2 text-[9px] text-emerald-500/70 uppercase tracking-wider">
              Admin: delete enabled
            </p>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-4 flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
              <ImageIcon size={18} className="text-amber-400" />
              {favoritesOnly
                ? 'Favorites'
                : currentFolder
                ? currentFolder.name
                : 'All Assets'}
            </h1>
            <p className="text-xs text-zinc-600">
              {filteredAssets.length} {filteredAssets.length === 1 ? 'item' : 'items'}
              {currentFolder && ` in ${currentFolder.name}`}
            </p>
          </div>

          <div className="flex-1" />

          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assets..."
              className="pl-8 pr-3 py-1.5 w-64 rounded-lg bg-white/[0.03] border border-white/[0.08] text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-500/30 transition-colors"
            />
          </div>

          <div className="flex gap-1 bg-white/[0.03] border border-white/[0.06] rounded-lg p-0.5">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.id ?? 'all'}
                onClick={() => setTypeFilter(opt.id)}
                className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                  typeFilter === opt.id
                    ? 'bg-amber-500/15 text-amber-300'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 text-xs font-medium hover:bg-amber-500/25 transition-colors"
          >
            <Upload size={14} />
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </header>

        <div
          className={`flex-1 overflow-y-auto sidebar-scroll p-6 relative ${
            dragOver ? 'bg-amber-500/5' : ''
          }`}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
              setDragOver(true);
            }
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-zinc-600" />
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <ImageIcon size={40} className="text-zinc-700 mb-3" />
              <p className="text-sm text-zinc-400 mb-1">
                {searchQuery
                  ? 'No assets match your search'
                  : currentFolder
                  ? `"${currentFolder.name}" is empty`
                  : 'Your asset library is empty'}
              </p>
              <p className="text-xs text-zinc-600 max-w-sm">
                Drop images or SVGs onto this area, or click{' '}
                <span className="text-amber-400">Upload</span> to build a
                shared library your whole design team can use.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]">
              {filteredAssets.map((asset) => (
                <AssetTile
                  key={asset.id}
                  asset={asset}
                  onInsert={handleInsert}
                />
              ))}
            </div>
          )}

          {dragOver && (
            <div className="absolute inset-6 flex items-center justify-center bg-amber-500/5 border-2 border-dashed border-amber-500/40 rounded-xl pointer-events-none">
              <div className="text-center">
                <Upload size={28} className="mx-auto mb-2 text-amber-400" />
                <p className="text-sm text-amber-300 font-medium">Drop to upload</p>
                <p className="text-[11px] text-amber-500/70 mt-1">
                  Large files will be compressed automatically
                </p>
              </div>
            </div>
          )}
        </div>

        <AssetUploadQueue />
      </div>

      {showSettings && <AssetCompressionSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
}
